"use client";

// import { roleLabels } from "@clutch/auth/rbac";
import { c as _c } from "react/compiler-runtime";
import type { OrganizationMembershipSummary } from "@clutch/auth/actions/users";
import { ArrowUpDownIcon, Tick02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useTransition } from "react";
import { toast } from "sonner";
import { getOrganizationMembershipsAction, switchActiveOrganizationAction } from "@/app/_actions/organizations";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton } from "@/components/ui/sidebar";
import { UserAvatar } from "@/components/ui/user-avatar";
import { extractDistrictSlug } from "@/config/sidebar-navigation";
import { authClient } from "@/lib/auth-client";
import { clearDashboardSwitch, startDashboardSwitch } from "@/lib/dashboard-loading-label";
export function ContextSwitcher() {
  const $ = _c(59);
  const pathname = usePathname();
  const router = useRouter();
  const {
    data: session
  } = authClient.useSession();
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] !== pathname) {
    t0 = extractDistrictSlug(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const activeDistrictSlug = t0;
  const isDistrictWorkspaceIndex = pathname === "/dashboard/d";
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["organization-memberships"],
      queryFn: _temp,
      staleTime: 300000
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: memberships,
    isLoading
  } = useQuery(t1);
  const isReady = !isLoading || memberships !== undefined;
  let t2;
  if ($[3] !== activeDistrictSlug || $[4] !== isDistrictWorkspaceIndex || $[5] !== memberships) {
    t2 = memberships?.find(m => m.districtSlug === activeDistrictSlug || m.organizationSlug === activeDistrictSlug) ?? (isDistrictWorkspaceIndex ? memberships?.find(_temp2) : undefined);
    $[3] = activeDistrictSlug;
    $[4] = isDistrictWorkspaceIndex;
    $[5] = memberships;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const activeMembership = t2;
  const isPlayerContext = !activeDistrictSlug && !isDistrictWorkspaceIndex;
  let t3;
  let t4;
  if ($[7] !== isPending) {
    t3 = () => {
      if (!isPending) {
        clearDashboardSwitch();
      }
    };
    t4 = [isPending];
    $[7] = isPending;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== pathname || $[11] !== router) {
    t5 = function handlePlayerSwitch() {
      startDashboardSwitch("Player Dashboard", pathname);
      router.push("/dashboard");
    };
    $[10] = pathname;
    $[11] = router;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handlePlayerSwitch = t5;
  let t6;
  if ($[13] !== pathname) {
    t6 = function handleOrgSwitch(membership_0) {
      const slug = membership_0.districtSlug ?? membership_0.organizationSlug;
      startDashboardSwitch(membership_0.organizationName, pathname);
      requestAnimationFrame(() => {
        startTransition(async () => {
          const formData = new FormData();
          formData.set("organizationId", membership_0.organizationId);
          formData.set("redirectTo", `/dashboard/d/${slug}`);
          const result = await switchActiveOrganizationAction(null, formData);
          if (result?.status === "error") {
            clearDashboardSwitch();
            toast.error(result.message);
          }
        });
      });
    };
    $[13] = pathname;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleOrgSwitch = t6;
  if (!isReady) {
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <SidebarMenu><SidebarMenuItem><SidebarMenuSkeleton showIcon={true} /></SidebarMenuItem></SidebarMenu>;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  const contextLabel = isPlayerContext ? "Player" : activeMembership?.organizationName ?? "Workspace";
  const contextSublabel = isPlayerContext ? "Personal account" : activeMembership ? `${activeMembership.district ?? activeMembership.organizationSlug}` : "District workspace";
  let t7;
  if ($[16] !== activeMembership?.districtSlug || $[17] !== activeMembership?.organizationLogo || $[18] !== activeMembership?.organizationName || $[19] !== isPlayerContext || $[20] !== session?.user.handle || $[21] !== session?.user.image) {
    t7 = isPlayerContext ? <UserAvatar className="size-9 shrink-0 rounded-full" image={session?.user.image} name="Player" seed={session?.user.handle} variant="beam" /> : <UserAvatar className="size-9 shrink-0 rounded-full" image={activeMembership?.organizationLogo} name={activeMembership?.organizationName} seed={activeMembership?.districtSlug} variant="marble" />;
    $[16] = activeMembership?.districtSlug;
    $[17] = activeMembership?.organizationLogo;
    $[18] = activeMembership?.organizationName;
    $[19] = isPlayerContext;
    $[20] = session?.user.handle;
    $[21] = session?.user.image;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== contextLabel) {
    t8 = <span className="truncate font-semibold">{contextLabel}</span>;
    $[23] = contextLabel;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== contextSublabel) {
    t9 = <span className="!text-muted-foreground truncate text-xs">{contextSublabel}</span>;
    $[25] = contextSublabel;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">{t8}{t9}</div>;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <HugeiconsIcon icon={ArrowUpDownIcon} className="ml-auto size-5 group-data-[collapsible=icon]:hidden" />;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== isPending || $[32] !== t10 || $[33] !== t7) {
    t12 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" className="h-fit data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground" disabled={isPending}>{t7}{t10}{t11}</SidebarMenuButton></DropdownMenuTrigger>;
    $[31] = isPending;
    $[32] = t10;
    $[33] = t7;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DropdownMenuLabel className="text-muted-foreground text-xs">Context</DropdownMenuLabel>;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = `cursor-pointer gap-3 p-2 ${isPlayerContext ? "bg-accent text-accent-foreground" : ""}`;
  const t15 = session?.user.image;
  const t16 = session?.user.handle;
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <UserAvatar className="size-8 shrink-0 rounded-full" image={t15} name="Player" seed={t16} variant="beam" />;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="grid flex-1 leading-tight"><span className="font-medium">Player</span><span className="!text-muted-foreground text-xs">Personal account</span></div>;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = `mr-1.5 ml-auto flex shrink-0 items-center justify-center text-foreground ${!isPlayerContext ? "invisible" : ""}`;
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <HugeiconsIcon icon={Tick02Icon} className="size-4" strokeWidth={2.5} />;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t19) {
    t21 = <span className={t19}>{t20}</span>;
    $[41] = t19;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== handlePlayerSwitch || $[44] !== t14 || $[45] !== t17 || $[46] !== t21) {
    t22 = <DropdownMenuItem onClick={handlePlayerSwitch} className={t14}>{t17}{t18}{t21}</DropdownMenuItem>;
    $[43] = handlePlayerSwitch;
    $[44] = t14;
    $[45] = t17;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== activeDistrictSlug || $[49] !== handleOrgSwitch || $[50] !== isPlayerContext || $[51] !== memberships) {
    t23 = memberships && memberships.length > 0 ? <><DropdownMenuSeparator /><DropdownMenuLabel className="text-muted-foreground text-xs">Organizations</DropdownMenuLabel>{memberships.map(membership_1 => {
        const slug_0 = membership_1.districtSlug ?? membership_1.organizationSlug;
        const isActive = !isPlayerContext && slug_0 === activeDistrictSlug;
        return <DropdownMenuItem key={membership_1.organizationId} onClick={() => handleOrgSwitch(membership_1)} className={`cursor-pointer gap-3 p-2 ${isActive ? "bg-accent text-accent-foreground" : ""}`}><UserAvatar className="size-8 shrink-0 rounded-full" image={membership_1.organizationLogo} name={membership_1.organizationName} seed={membership_1.districtSlug} variant="marble" /><div className="grid flex-1 leading-tight"><span className="font-medium">{membership_1.organizationName}</span><span className="!text-muted-foreground text-xs">{membership_1.district && membership_1.state ? `${membership_1.district}, ${membership_1.state}` : membership_1.organizationSlug}</span></div><span className={`mr-1.5 ml-auto flex shrink-0 items-center justify-center text-foreground ${!isActive ? "invisible" : ""}`}><HugeiconsIcon icon={Tick02Icon} className="size-4" strokeWidth={2.5} /></span></DropdownMenuItem>;
      })}</> : null;
    $[48] = activeDistrictSlug;
    $[49] = handleOrgSwitch;
    $[50] = isPlayerContext;
    $[51] = memberships;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t22 || $[54] !== t23) {
    t24 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 font-jakarta" align="start" side="bottom" sideOffset={4}>{t13}{t22}{t23}</DropdownMenuContent>;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t12 || $[57] !== t24) {
    t25 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t12}{t24}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[56] = t12;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}
function _temp2(membership) {
  return membership.isActive;
}
function _temp() {
  return getOrganizationMembershipsAction();
}